/**
 * User-friendly error message mapper.
 *
 * Converts raw API / OAuth error messages into plain English that tells
 * the user what went wrong and what to do about it.
 *
 * Usage:
 *   catch (err) {
 *     toast.toast(friendlyError(err), 'error');
 *   }
 */

const ERROR_MAP: Array<{ pattern: RegExp; message: string }> = [
  // Auth / token errors
  { pattern: /invalid.?auth|invalid.?token|INVALID_AUTH/i, message: 'Your session expired. Please sign in again.' },
  { pattern: /unauthorized|401/i, message: 'You need to sign in to do that.' },
  { pattern: /forbidden|403/i, message: "You don't have permission for this action. Ask your workspace admin." },
  { pattern: /invalid.?grant/i, message: 'Your connection expired. Please reconnect in Settings → Integrations.' },

  // MFA / two-factor step-up
  { pattern: /aal2|assurance level/i, message: 'Enter the 6-digit code from your authenticator app to confirm this change.' },
  { pattern: /invalid.*(totp|mfa|otp)|(totp|mfa|otp).*(invalid|incorrect|expired)|invalid.*code/i, message: 'That authenticator code is incorrect or expired. Enter the current code from your app.' },

  // OAuth / integration errors
  { pattern: /bad_state|invalid_state/i, message: 'The connection timed out. Please try connecting again.' },
  { pattern: /invalid_scope|scope.*denied/i, message: "We need additional permissions to connect. Please try again and approve all requested permissions." },
  { pattern: /access_denied|user_denied/i, message: 'Connection was cancelled. You can try again anytime.' },
  { pattern: /already.?connected|duplicate/i, message: 'This integration is already connected.' },
  { pattern: /webhook.*secret.*not.*configured/i, message: 'This integration is not configured yet. Contact support.' },
  { pattern: /location id.*not found|enter your location id/i, message: 'Enter your Location ID — find it in GoHighLevel → Settings → Business Profile.' },
  { pattern: /GoHighLevel rejected/i, message: 'GoHighLevel rejected the token. Check the token and Location ID, and that the private integration has the View Contacts and View Opportunities scopes.' },

  // Rate limiting
  { pattern: /too many requests|rate.?limit|429/i, message: "You're doing that too fast. Wait a moment and try again." },

  // Network / server errors
  { pattern: /network|fetch failed|failed to fetch|ECONNRESET/i, message: "Can't reach the server. Check your internet connection and try again." },
  { pattern: /timeout|timed out|504/i, message: 'The request timed out. Try again in a moment.' },
  { pattern: /500|internal server/i, message: 'Something went wrong on our end. Try again, or contact support if it persists.' },
  { pattern: /503|service unavailable/i, message: 'The service is temporarily unavailable. Try again in a few minutes.' },

  // Payload errors
  { pattern: /payload too large|413/i, message: 'That file is too large. Maximum size is 10 MB.' },
  { pattern: /invalid.?json|JSON.parse/i, message: 'Something went wrong with the request. Please try again.' },

  // Billing
  { pattern: /subscription.*required|no.*active.*subscription/i, message: 'This feature requires an active subscription. Visit Settings → Billing to subscribe.' },
  { pattern: /trial.*expired/i, message: 'Your free trial has ended. Subscribe to continue using Waypoint.' },

  // Data
  { pattern: /not.?found|404/i, message: "That item couldn't be found. It may have been deleted." },
  { pattern: /conflict|409/i, message: 'This conflicts with a recent change. Refresh and try again.' },
];

/**
 * Convert an error (string, Error, ApiError, unknown) into a user-friendly message.
 */
export function friendlyError(err: unknown): string {
  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : String(err);

  for (const { pattern, message } of ERROR_MAP) {
    if (pattern.test(raw)) return message;
  }

  // If the raw message is short and doesn't look too technical, use it
  if (raw.length < 80 && !/[{}<>]|Error:|at\s+\w/.test(raw)) {
    return raw;
  }

  return 'Something went wrong. Please try again.';
}
